import React, { useCallback, useEffect, useState } from 'react';

import { Divider, Group, Progress, Stack, Text } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconFileText } from '@tabler/icons-react';

import {
  useConfirmUploadMutation,
  useGenerateUploadUrlMutation
} from '@/store/slices/api/patient/patientDocumentsApiSlice';

import Dropzone from '@/ui/Dropzone';
import IconButton from '@/ui/IconButton';
import CustomModal from '@/ui/Modal';
import TextInputWrapper from '@/ui/TextInputWrapper';
import TextWrapper from '@/ui/TextWrapper';

import styles from './DocumentUploadModal.module.scss';

interface DocumentUploadModalProps {
  isOpen: boolean;
  onClose: () => void;
  patientId: string;
  onUploadSuccess?: () => void;
}

const DocumentUploadModal: React.FC<DocumentUploadModalProps> = ({
  isOpen,
  onClose,
  patientId,
  onUploadSuccess
}) => {
  const [isUploading, setIsUploading] = useState(false);
  const [documentName, setDocumentName] = useState('');
  const [addedFiles, setAddedFiles] = useState<File[]>([]);
  const [uploadProgress, setUploadProgress] = useState(0);

  const [generateUploadUrl] = useGenerateUploadUrlMutation();
  const [confirmUpload] = useConfirmUploadMutation();

  // Reset form when modal opens/closes
  useEffect(() => {
    if (!isOpen) {
      setDocumentName('');
      setAddedFiles([]);
      setUploadProgress(0);
      setIsUploading(false);
    }
  }, [isOpen]);

  const validateFile = (file: File): string | null => {
    // Check file type
    if (!file.type.includes('pdf')) {
      return 'Only PDF files are allowed';
    }

    return null;
  };

  const validateFileName = (fileName: string): string | null => {
    const trimmedName = fileName.trim();

    if (!trimmedName) {
      return 'Document name is required';
    }

    if (trimmedName.length > 250) {
      // Leave some room for .pdf extension
      return 'Document name is too long (maximum 250 characters)';
    }

    // Check for invalid characters that might cause issues
    // eslint-disable-next-line no-control-regex
    const invalidChars = /[<>:"/\\|?*\x00-\x1f]/;
    if (invalidChars.test(trimmedName)) {
      return 'Document name contains invalid characters';
    }

    return null;
  };

  // Helper function to get the full filename with .pdf extension
  const getFullFileName = (name: string): string => {
    const trimmedName = name.trim();
    return trimmedName.toLowerCase().endsWith('.pdf') ? trimmedName : `${trimmedName}.pdf`;
  };

  const uploadFileToS3 = async (uploadUrl: string, file: File): Promise<void> => {
    try {
      const response = await fetch(uploadUrl, {
        method: 'PUT',
        body: file,
        headers: {
          'Content-Type': file.type || 'application/pdf'
        }
      });

      if (!response.ok) {
        const errorText = await response.text().catch(() => 'Unknown error');
        throw new Error(
          `Upload failed with status: ${response.status} - ${response.statusText}. ${errorText}`
        );
      }
      setUploadProgress(100);
    } catch (error) {
      console.error('S3 Upload Error:', error);
      throw error;
    }
  };

  const handleDocumentNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setDocumentName(value);
  };

  const handleUpload = async () => {
    const fileNameValidationError = validateFileName(documentName);
    if (fileNameValidationError) {
      notifications.show({
        title: 'Invalid Document Name',
        message: fileNameValidationError,
        color: 'red'
      });
      return;
    }

    if (addedFiles.length === 0) {
      notifications.show({
        title: 'Error',
        message: 'Please select a file to upload',
        color: 'red'
      });
      return;
    }

    const file = addedFiles[0];
    const fileValidationError = validateFile(file);
    if (fileValidationError) {
      notifications.show({
        title: 'File Error',
        message: fileValidationError,
        color: 'red'
      });
      return;
    }

    setIsUploading(true);
    setUploadProgress(0);

    try {
      // Step 1: Generate upload URL with .pdf extension
      const fullFileName = getFullFileName(documentName);
      const uploadUrlResponse = await generateUploadUrl({
        patientId,
        fileName: fullFileName
      }).unwrap();

      // Step 2: Upload file to S3
      await uploadFileToS3(uploadUrlResponse.uploadUrl, file);

      // Step 3: Confirm upload
      await confirmUpload(uploadUrlResponse.id).unwrap();

      // Call success callback and close modal
      if (onUploadSuccess) {
        onUploadSuccess();
      }
      onClose();
    } catch (error) {
      console.error('Upload error:', error);
      notifications.show({
        title: 'Upload Failed',
        message: error instanceof Error ? error.message : 'Failed to upload document',
        color: 'red'
      });
    } finally {
      setIsUploading(false);
      setUploadProgress(0);
    }
  };

  const handleClose = () => {
    if (!isUploading) {
      onClose();
    }
  };

  const cancelButton = () => {
    return {
      text: 'Cancel',
      onClick: handleClose,
      isDisabled: isUploading
    };
  };

  const saveButton = () => {
    const fileNameError = validateFileName(documentName);
    const isDisabled = fileNameError !== null || addedFiles.length === 0 || isUploading;

    return {
      text: isUploading ? 'Uploading...' : 'Upload',
      onClick: handleUpload,
      isDisabled
    };
  };

  const handleFilesSelected = useCallback(
    (files: FileList) => {
      if (!files || files.length === 0) {
        return;
      }

      try {
        const newFiles: File[] = [];

        for (const file of Array.from(files)) {
          if (!file.type.includes('pdf')) {
            notifications.show({
              title: 'Invalid File Type',
              message: `${file.name} is not a PDF file and was skipped`,
              color: 'orange'
            });
            continue;
          }

          const validationError = validateFile(file);
          if (validationError) {
            notifications.show({
              title: 'File Error',
              message: `${file.name}: ${validationError}`,
              color: 'red'
            });
            continue;
          }

          newFiles.push(file);
        }

        if (newFiles.length > 0) {
          setAddedFiles(newFiles);

          // Auto-populate document name if empty (without .pdf extension)
          if (!documentName && newFiles[0]) {
            const fileName = newFiles[0].name;
            // Remove .pdf extension from the display name
            const nameWithoutExtension = fileName.toLowerCase().endsWith('.pdf')
              ? fileName.slice(0, -4)
              : fileName.replace(/\.[^/.]+$/, '');
            setDocumentName(nameWithoutExtension);
          }
        }
      } catch (error) {
        console.error('Error in file input handling:', error);
        notifications.show({
          title: 'Error',
          message: 'An unexpected error occurred while processing your files',
          color: 'red'
        });
      }
    },
    [documentName]
  );

  const handleRemoveFile = (indexToRemove: number) => {
    setAddedFiles((prev) => prev.filter((_, index) => index !== indexToRemove));
  };

  const modalContent = () => {
    return (
      <Stack p="lg">
        <TextInputWrapper
          label="Document Name"
          placeholder="Please provide the name"
          value={documentName}
          onChange={handleDocumentNameChange}
          className={styles.titleInput}
          disabled={isUploading}
          required
        />

        <Divider />

        <Dropzone
          onFilesSelected={handleFilesSelected}
          onPaste={() => {}}
          isUploading={isUploading}
          acceptedFileTypes="application/pdf"
          disabled={addedFiles.length >= 1 || isUploading}
          maxFiles={1}
        />

        {addedFiles.length > 0 && (
          <Stack className={styles.stack}>
            <TextWrapper text="Selected Document" variant="heading" />
            {addedFiles.map((file, index) => (
              <Group
                key={`${file.name}-${index}`}
                className={styles.fileItem}
                justify="space-between">
                <Group>
                  <IconFileText size={18} />
                  <div>
                    <Text size="sm" fw={500}>
                      {file.name}
                    </Text>
                    <Text size="xs" c="dimmed">
                      {(file.size / 1024 / 1024).toFixed(2)} MB
                    </Text>
                  </div>
                </Group>
                {!isUploading && (
                  <IconButton icon="close" onClick={() => handleRemoveFile(index)} />
                )}
              </Group>
            ))}
          </Stack>
        )}

        {isUploading && (
          <Stack gap="xs">
            <Text size="sm" fw={500}>
              Upload Progress
            </Text>
            <Progress value={uploadProgress} size="lg" striped animated />
          </Stack>
        )}
      </Stack>
    );
  };

  return (
    <CustomModal
      modalProps={{
        opened: isOpen,
        onClose: handleClose,
        size: 'xl',
        closeOnClickOutside: !isUploading,
        closeOnEscape: !isUploading
      }}
      header={{ title: 'Upload Document' }}
      secondaryButton={cancelButton()}
      primaryButton={saveButton()}>
      {modalContent()}
    </CustomModal>
  );
};

export default DocumentUploadModal;
